import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useQuery } from '@apollo/client';
import { useRouteMatch, NavLink, useLocation, Redirect } from 'react-router-dom';
import sortBy from 'lodash/sortBy';

import { menuPath, menuCategoryPath, menuFilterPath } from 'core/api/actions/navigation';
import { LIST_PRODUCT_CATEGORIES, LIST_PRODUCT_TAGS } from 'core/api/apollo/queries';
import { Flex, Container, List, ListItem, Icon } from 'core/ui/atoms';

function ProductListNavBar(props) {
  const { deliveryDay } = props;

  const location = useLocation();
  const match = useRouteMatch();
  const { params: { categoryId: pageId } } = match;
  const searchParams = new URLSearchParams(location.search);
  const categoriesRef = React.useRef(null);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const isShipping = searchParams.get('delivery');

  const {
    data: { listProductCategories: categories = [] } = {},
  } = useQuery(LIST_PRODUCT_CATEGORIES, { variables: { deliveryDayId: deliveryDay.id } });
  const {
    data: { listProductTags: filters = [] } = {},
  } = useQuery(LIST_PRODUCT_TAGS, { variables: { deliveryDayId: deliveryDay.id } });

  const promoFilters = sortBy(filters.filter((filter) => filter.promo), ['tagGroup.position', 'position']);

  const programCategory = categories.find((c) =>{
    return c.name.toLowerCase().includes('programs');
  });

  const HIDDEN_CATEGORY_NAMES = ['made with love', 'the shop'];
  const visibleCategories = categories.filter((c) => (
    !HIDDEN_CATEGORY_NAMES.includes(c.name.toLowerCase())
  ));

  const updateScrollState = React.useCallback(() => {
    const list = categoriesRef.current;
    if (!list) {
      return;
    }

    const maxScrollLeft = list.scrollWidth - list.clientWidth;
    const overflow = list.scrollWidth > list.clientWidth + 1;
    setHasOverflow(overflow);
    setCanScrollLeft(list.scrollLeft > 0);
    setCanScrollRight(list.scrollLeft < maxScrollLeft - 1);
  }, []);

  React.useEffect(() => {
    updateScrollState();

    const list = categoriesRef.current;
    if (!list) {
      return undefined;
    }

    const handleScroll = () => updateScrollState();
    list.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      list.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, categories.length, promoFilters.length]);

  const handleScroll = (direction) => {
    const list = categoriesRef.current;
    if (!list) {
      return;
    }

    const amount = direction === 'left' ? -240 : 240;
    list.scrollBy({ left: amount, behavior: 'smooth' });
  };

  if (isShipping && programCategory) {
    const zipcode = searchParams.get('zipcode');
    const queryParams = isShipping ? { delivery: isShipping, zipcode } : {};

    return (
      <Redirect to={menuCategoryPath(deliveryDay.id, programCategory.id, queryParams)}/>
    );
  }

  return (
    <NavWrapper>
      <NavContainer>
        {hasOverflow && (
          <>
            <ScrollButton
              type="button"
              aria-label="Scroll categories left"
              $position="left"
              disabled={!canScrollLeft}
              onClick={() => handleScroll('left')}
            >
              <ScrollIcon icon="arrow_l" />
            </ScrollButton>
            <ScrollButton
              type="button"
              aria-label="Scroll categories right"
              $position="right"
              disabled={!canScrollRight}
              onClick={() => handleScroll('right')}
            >
              <ScrollIcon icon="arrow_r" />
            </ScrollButton>
          </>
        )}
        <CategoriesList unstyled horizontal centered ref={categoriesRef}>
          <CategoryItem key={`category-nav-${pageId}`}>
            <CategoryLink to={menuPath(deliveryDay.id)} exact>
              Weekly Highlights
            </CategoryLink>
          </CategoryItem>

          {promoFilters.map((filter) => (
            <CategoryItem key={`filter-nav-${pageId}-${filter.id}`}>
              <CategoryLink to={menuFilterPath(deliveryDay.id, filter.id)}>{filter.name}</CategoryLink>
            </CategoryItem>
          ))}

          {visibleCategories.map((category) => (
            <CategoryItem key={`category-nav-${pageId}-${category.id}`}>
              <CategoryLink to={menuCategoryPath(deliveryDay.id, category.id)}>{category.name}</CategoryLink>
            </CategoryItem>
          ))}
        </CategoriesList>
      </NavContainer>
    </NavWrapper>
  );
}

ProductListNavBar.propTypes = {
  deliveryDay: PropTypes.object.isRequired,
};

const NavWrapper = styled(Flex)`
  position: sticky;
  background: white;
  z-index: ${(p) => p.theme.zIndex.headerFixed - 1};
  top: calc(${(p) => p.theme.layout.headerHeight} * 2 - 1px);
  width: 100%;
  align-items: center;
  min-height: ${(p) => p.theme.layout.headerHeight};
  border-bottom: ${(p) => p.theme.borders.width} solid ${(p) => p.theme.colors.grays.light};
`;

const BookendStyles = css`
  content: '';
  position: absolute;
  top: 0;
  height: 100%;
  width: 5vw;
  ${(p) => p.theme.min('md')`
    width: 3vw;
  `};
  z-index: 1000;
`;

const NavContainer = styled(Container)`
  display: flex;
  align-items: center;
  padding: 0 !important;

  position: relative;
  &:before {
    ${BookendStyles}
    left: 0;
    background: linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 50%, rgba(255,255,255,0) 100%);
  }

  &:after {
    ${BookendStyles}
    right: 0;
    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 50%, rgba(255,255,255,1) 100%);
  }
`;

const CategoriesList = styled(List)`
  overflow: auto;
  flex-wrap: nowrap;
  justify-content: initial;
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

const CategoryItem = styled(ListItem)`
  white-space: nowrap;
  padding: 10px 20px 0 20px;
  ${(p) => p.theme.max('md')`
    padding-right: 10px;
    padding-left: 10px;
  `};

  &:first-child {
    padding-left: 5vw;
    ${(p) => p.theme.min('md')`
      padding-left: 3vw;
    `};
  }

  &:last-child {
    padding-right: 5vw;
    ${(p) => p.theme.min('md')`
      padding-right: 3vw;
    `};
  }
`;

const CategoryLink = styled(NavLink)`
  position: relative;
  font-size: ${(p) => p.theme.typography.sizes.h5};
  font-weight: bold;
  text-decoration: none;
  display: block;
  color: ${(p) => p.theme.typography.colors.primary};

  &.active, &:hover {
    &:after {
      position: absolute;
      content: '';
      display: block;
      height: 1px;
      width: 100%;
      left: 0;
      bottom: 0;
      background: ${(p) => p.theme.colors.primary};
    }
  }
`;

const ScrollButton = styled.button`
  position: absolute;
  top: 50%;
  ${(p) => (p.$position === 'left' ? 'left: 6px;' : 'right: 6px;')}
  transform: translateY(-50%);
  height: 32px;
  width: 32px;
  border-radius: 50%;
  border: ${(p) => p.theme.borders.width} solid ${(p) => p.theme.colors.grays.light};
  background: ${(p) => p.theme.colors.white};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  z-index: 1001;
  cursor: pointer;
  padding: 0;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const ScrollIcon = styled(Icon)`
  font-size: 0.7em;
`;

export default ProductListNavBar;
